import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import {
  createMedicationServiceClient,
  type DoseEvent,
  type Medication,
  type MedicationInput,
  type UpcomingDose,
} from "./client";

export type MedicationsStatus = "idle" | "loading" | "ready" | "saving" | "error";

export function useMedications() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.medicationService?.apiBaseUrl;
  const [medications, setMedications] = useState<Medication[]>([]);
  const [upcomingDoses, setUpcomingDoses] = useState<UpcomingDose[]>([]);
  const [doseEventsByMedication, setDoseEventsByMedication] = useState<Record<string, DoseEvent[]>>({});
  const [status, setStatus] = useState<MedicationsStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createMedicationServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadMedications = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);
      setSaved(false);

      try {
        const [records, upcoming] = await Promise.all([client.listMedications(signal), client.listUpcomingDoses(signal)]);
        setMedications(records);
        setUpcomingDoses(upcoming);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your medication records.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadMedications(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadMedications]);

  useEffect(() => {
    if (!saved) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setSaved(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [saved]);

  const refreshUpcomingDoses = useCallback(async () => {
    if (!client) {
      return;
    }

    try {
      setUpcomingDoses(await client.listUpcomingDoses());
    } catch {
      // Keep the current dashboard state if the refresh fails after a write.
    }
  }, [client]);

  const createMedication = useCallback(
    async (payload: MedicationInput) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.createMedication(payload);
        setMedications((current) => [record, ...current]);
        await refreshUpcomingDoses();
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the medication record.");
        setStatus("ready");
        return null;
      }
    },
    [client, refreshUpcomingDoses],
  );

  const updateMedication = useCallback(
    async (medicationId: string, payload: Partial<MedicationInput>) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.updateMedication(medicationId, payload);
        setMedications((current) => current.map((item) => (item.medication_id === medicationId ? record : item)));
        await refreshUpcomingDoses();
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the medication record.");
        setStatus("ready");
        return null;
      }
    },
    [client, refreshUpcomingDoses],
  );

  const setMedicationLifecycle = useCallback(
    async (medicationId: string, lifecycle: "completed" | "stopped") => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = lifecycle === "completed" ? await client.completeMedication(medicationId) : await client.stopMedication(medicationId);
        setMedications((current) => current.map((item) => (item.medication_id === medicationId ? record : item)));
        await refreshUpcomingDoses();
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not update the medication status.");
        setStatus("ready");
        return null;
      }
    },
    [client, refreshUpcomingDoses],
  );

  const loadDoseEvents = useCallback(
    async (medicationId: string, signal?: AbortSignal) => {
      if (!client) {
        return [];
      }

      const events = await client.listDoseEvents(medicationId, signal);
      setDoseEventsByMedication((current) => ({ ...current, [medicationId]: events }));
      return events;
    },
    [client],
  );

  const recordDoseEvent = useCallback(
    async (
      medicationId: string,
      occurrenceKey: string,
      payload: Parameters<ReturnType<typeof createMedicationServiceClient>["putDoseEvent"]>[2],
    ) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);

      try {
        const event = await client.putDoseEvent(medicationId, occurrenceKey, payload);
        setDoseEventsByMedication((current) => ({
          ...current,
          [medicationId]: [
            event,
            ...(current[medicationId] ?? []).filter((item) => item.occurrence_key !== event.occurrence_key),
          ],
        }));
        await refreshUpcomingDoses();
        setStatus("ready");
        setSaved(true);
        return event;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not update that dose.");
        setStatus("ready");
        return null;
      }
    },
    [client, refreshUpcomingDoses],
  );

  return {
    createMedication,
    doseEventsByMedication,
    error,
    loadDoseEvents,
    loadMedications,
    medications,
    recordDoseEvent,
    saved,
    setMedicationLifecycle,
    status,
    upcomingDoses,
    updateMedication,
  };
}
